import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import supabase from '../lib/supabase';
import LoginGateModal from './LoginGateModal';

import '../styles/ProjectManager.css';
import '../styles/ProCalculatorFooter.css';

const ProjectManager = ({ 
  calculatorState, 
  onLoadProject,
  onProjectAction,
  onClearAllData,
  isSubscribed = false,
  showSaveForm: propShowSaveForm,
  setShowSaveForm: propSetShowSaveForm,
  onProjectUpdated
}) => {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [planName, setPlanName] = useState('');
  const [planDescription, setPlanDescription] = useState('');
  // Use props for showSaveForm if provided, otherwise use local state
  const [localShowSaveForm, setLocalShowSaveForm] = useState(false);
  const showSaveForm = propShowSaveForm !== undefined ? propShowSaveForm : localShowSaveForm;
  const setShowSaveForm = propSetShowSaveForm || setLocalShowSaveForm;
  const [showCreateNewForm, setShowCreateNewForm] = useState(false);
  const [showLoginGate, setShowLoginGate] = useState(false);
  const [currentPlanId, setCurrentPlanId] = useState(null);
  const [localBackups, setLocalBackups] = useState([]);
  const [viewMode, setViewMode] = useState('row'); // 'card' or 'row'
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 768);
  const presMobileViewMode = useRef('row'); // remembers desktop preference before mobile forced it
  const [showDuplicateForm, setShowDuplicateForm] = useState(false);
  const [duplicatingPlan, setDuplicatingPlan] = useState(null);
  const [duplicateName, setDuplicateName] = useState('');
  const [duplicateDescription, setDuplicateDescription] = useState('');
  const [duplicating, setDuplicating] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  // Log calculatorState when save form is shown
  useEffect(() => {
    if (showSaveForm) {
    }
  }, [showSaveForm, calculatorState]);

  // Fetch user's projects
  useEffect(() => {
    let isMounted = true;
    
    const loadProjects = async () => {
      if (!user) {
        setLoading(false);
        setPlans([]);
        return;
      }
      
      setLoading(true);
      
      try {
        await fetchPlans();
      } catch (error) {
        if (isMounted) {
          setLoading(false);
        }
      }
      // Note: setLoading(false) is primarily handled by fetchPlans' finally block.
    };
    
    loadProjects();
    
    return () => {
      isMounted = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- 3.4.2026
  }, [user]); // Only re-run when user changes

  // Load local backups on mount
  useEffect(() => {
    const savedBackups = localStorage.getItem('backupPlans');
    if (savedBackups) {
      try {
        const parsedBackups = JSON.parse(savedBackups);
        setLocalBackups(parsedBackups);
      } catch (error) {
      }
    }
  }, []);

  // Force card view on mobile; restore desktop preference when resizing back
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobileView(mobile);
      if (mobile) {
        setViewMode('card');
      } else {
        setViewMode(presMobileViewMode.current);
      }
    };

    // Apply on mount
    if (window.innerWidth <= 768) {
      setIsMobileView(true);
      setViewMode('card');
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPlans = async () => {
    if (!user) {
      return; // Should not happen if useEffect guard is working
    }
    
    // setLoading(true); // Moved to the useEffect to avoid potential rapid toggles if fetchPlans is called elsewhere
    
    try {
      const { data, error } = await supabase
        .from('projects')
        .select('id, name, created_at, updated_at, data, description')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false });
      
      if (error) {
        if (error.code === '42P01') {
        }
        setPlans([]); // Ensure projects is empty on error
        return; // Exit if there's an error
      } else {
        setPlans(data || []);
      }
    } catch (error) {
      setPlans([]); // Ensure projects is empty on critical error
    } finally {
      setLoading(false); // Ensure loading is always set to false after fetch attempt
    }
  };

  const handleSavePlan = async (e) => {
    e.preventDefault();
    if (!user) {
      onProjectAction('save', 'You must be logged in to save plans');
      return;
    }
    
    if (!planName.trim()) {
      onProjectAction('save', 'Please enter a plan name');
      return;
    }
    
    setSaving(true);
    
    try {
      // DEBUG: Log the calculatorState received by the save function

      // Prepare the project data
      // On rename, only update name/description — never overwrite stored calculator data
      const planData = currentPlanId
        ? {
            name: planName.trim(),
            description: planDescription.trim(),
            updated_at: new Date().toISOString(),
          }
        : {
            user_id: user.id,
            name: planName.trim(),
            description: planDescription.trim(),
            data: JSON.stringify(calculatorState),
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          };
      
      // Create a timeout promise
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Save request timed out after 20 seconds')), 20000);
      });
      
      let dbPromise;
      if (currentPlanId) {
        // Update existing project
        dbPromise = supabase
          .from('projects')
          .update(planData)
          .eq('id', currentPlanId)
          .eq('user_id', user.id)
          .select();
      } else {
        // Create new project
        dbPromise = supabase
          .from('projects')
          .insert([planData])
          .select();
      }
      
      // Race the database operation against the timeout
      const result = await Promise.race([
        dbPromise,
        timeoutPromise.then(() => ({ data: null, error: new Error('Save request timed out after 20 seconds') }))
      ]);
      
      // eslint-disable-next-line no-unused-vars -- 3.4.2026
      const { data, error } = result;

      if (error) {
        throw error;
      }
      
      if (!error) {
        const savedName = planName.trim();
        const savedPlanId = currentPlanId;

        // Close the form immediately
        setPlanName('');
        setPlanDescription('');
        setCurrentPlanId(null);
        setShowSaveForm(false);

        // Always reload from DB to guarantee the list is fresh
        const { data: refreshed } = await supabase
          .from('projects')
          .select('id, name, created_at, updated_at, data, description')
          .eq('user_id', user.id)
          .order('updated_at', { ascending: false });
        if (refreshed) {
          setPlans([...refreshed]);
        }

        if (savedPlanId) {
          if (onProjectUpdated) {
            onProjectUpdated(savedPlanId, savedName);
          }
        } else {
          onProjectAction('save', 'Plan saved successfully');
        }
        
      } else {
        // Save to localStorage as a backup
        saveToLocalStorage(planData);
      }
    } catch (error) {
      // Try to save to localStorage as a last resort
      try {
        const planData = {
          user_id: user.id,
          name: planName.trim(),
          description: planDescription.trim(),
          data: JSON.stringify(calculatorState),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
        
        saveToLocalStorage(planData);
        
        // Reset the form
        setPlanName('');
        setPlanDescription('');
        setCurrentPlanId(null);
        setShowSaveForm(false);
      } catch (localError) {
      }
    } finally {
      setSaving(false);
    }
  };
  
  const handleEditPlanClick = (planToEdit) => {
    setPlanName(planToEdit.name);
    setPlanDescription(planToEdit.description || '');
    setCurrentPlanId(planToEdit.id);
    setShowSaveForm(true);
  };

  // Helper function to save project to localStorage as a backup
  const saveToLocalStorage = (planData) => {
    try {
      const backups = JSON.parse(localStorage.getItem('backupPlans') || '[]');
      const newBackup = {
        ...planData,
        id: `local-${Date.now()}`,
      };
      backups.unshift(newBackup);
      localStorage.setItem('backupPlans', JSON.stringify(backups));
      setLocalBackups(backups);
      onProjectAction('save', 'Plan saved locally (offline mode)');
    } catch (e) {
      onProjectAction('save', 'Error: Could not save plan');
    }
  };

  // Handle direct project update without showing form
  // eslint-disable-next-line no-unused-vars -- 3.4.2026
  const handleUpdatePlan = async (project) => {
    if (!user) {
      onProjectAction('update', 'You must be logged in to update plans');
      return;
    }
    
    setSaving(true);
    
    try {
      // Prepare the project data - keep the same name and description
      const planData = {
        data: JSON.stringify(calculatorState),
        updated_at: new Date().toISOString(),
      };
      
      // Update existing project
      const { data, error } = await supabase
        .from('projects')
        .update(planData)
        .eq('id', project.id)
        .eq('user_id', user.id)
        .select();
      
      if (error) {
        throw error;
      }
      
      if (data && data.length > 0) {
        // Successfully updated in database
        setPlans(prevPlans => 
          prevPlans.map(p => p.id === project.id ? data[0] : p)
        );
        onProjectAction('update', `Plan "${project.name}" updated successfully`);
      }
    } catch (error) {
      onProjectAction('update', `Error updating project: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };
  
  // Handle loading a project
  const handleDuplicatePlanClick = (project) => {
    setDuplicatingPlan(project);
    setDuplicateName(`${project.name} (Copy)`);
    setDuplicateDescription(project.description || '');
    setShowDuplicateForm(true);
  };

  const handleDuplicateSubmit = async (e) => {
    e.preventDefault();
    if (!user || !duplicatingPlan) return;
    setDuplicating(true);
    try {
      const sourceData = typeof duplicatingPlan.data === 'string'
        ? duplicatingPlan.data
        : JSON.stringify(duplicatingPlan.data);
      const { data, error } = await supabase
        .from('projects')
        .insert([{
          user_id: user.id,
          name: duplicateName.trim(),
          description: duplicateDescription.trim(),
          data: sourceData,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        }])
        .select();
      if (error) throw error;
      if (data && data.length > 0) {
        setShowDuplicateForm(false);
        setDuplicatingPlan(null);
        setDuplicateName('');
        setDuplicateDescription('');
        await fetchPlans();
        showToast(`Plan "${duplicateName.trim()}" created successfully!`, 'success');
      }
    } catch (err) {
      showToast('Error duplicating plan: ' + err.message, 'error');
    } finally {
      setDuplicating(false);
    }
  };

  const handleLoadPlan = async (project, destination = 'reference') => {
    if (!user) return;
    
    setLoading(true);
    
    try {
      // Handle loading project data
      if (project && typeof project === 'object') {
        // If it's a local backup with isLocalBackup flag
        if (project.isLocalBackup) {
          try {
            // Parse the data if it's a string
            const planData = typeof project.data === 'string' ? JSON.parse(project.data) : project.data;
            
            // Load the project data into the calculator
            onLoadProject(planData, project.id, project.name, destination);
            onProjectAction('load', `Loaded local backup: ${project.name}`);
            
            // Also try to save it to the database in the background
            const { error } = await supabase
              .from('projects')
              .insert([{
                user_id: user.id,
                name: project.name,
                description: project.description || '',
                data: typeof project.data === 'string' ? project.data : JSON.stringify(project.data),
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              }]);
              
            if (!error) {
              // Successfully synced to cloud
              onProjectAction('sync', `Synced "${project.name}" to cloud storage`);
              
              // Remove from localStorage
              const updatedBackups = localBackups.filter(p => p.id !== project.id);
              localStorage.setItem('backupPlans', JSON.stringify(updatedBackups));
              setLocalBackups(updatedBackups);
            }
          } catch (error) {
            onProjectAction('load', `Error loading backup: ${error.message}`);
          }
        } else {
          // Regular project from database — always fetch fresh by ID to avoid stale state
          try {
            const { data: freshData, error: fetchError } = await supabase
              .from('projects')
              .select('id, name, description, data, created_at, updated_at')
              .eq('id', project.id)
              .eq('user_id', user.id)
              .single();

            if (fetchError) throw fetchError;

            const planData = typeof freshData.data === 'string' ? JSON.parse(freshData.data) : freshData.data;
            onLoadProject(planData, freshData.id, freshData.name, destination);
            onProjectAction('load', `Loaded project: ${freshData.name}`);
          } catch (error) {
            onProjectAction('load', `Error loading project: ${error.message}`);
          }
        }
      } else if (typeof project === 'string') {
        // If we received a project ID instead of a project object
        try {
          // Create a timeout promise
          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('Request timed out after 15 seconds')), 15000);
          });
          
          // Create the Supabase query promise
          // eslint-disable-next-line no-unused-vars -- 3.4.2026
          const startTime = Date.now();
          const queryPromise = supabase
            .from('projects')
            .select('*')
            .eq('id', project)
            .eq('user_id', user.id)
            .limit(1);
          
          // Race the query against the timeout
          const result = await Promise.race([
            queryPromise,
            timeoutPromise.then(() => ({ data: null, error: new Error('Request timed out after 15 seconds') }))
          ]);
          
          const { data, error } = result;
          
          if (error) {
            throw error;
          }
          
          if (data && data.length > 0) {
            // Parse the data if it's a string
            let planData;
            try {
              planData = typeof data[0].data === 'string' ? JSON.parse(data[0].data) : data[0].data;
              if (!planData || typeof planData !== 'object') {
                throw new Error('Invalid project data format');
              }
            } catch (e) {
              onProjectAction('load', 'Error: Could not load project data');
              return;
            }
            
            onLoadProject(planData, data[0].id, data[0].name);
            
            // Notify parent component that a project was loaded
            onProjectAction('load', `Loaded project: ${data[0].name}`);
          } else {
            throw new Error('Project not found');
          }
        } catch (error) {
          onProjectAction('load', `Error: ${error.message}`);
        }
      } else {
      }
    } catch (error) {
      setLoading(false);
      // Try to load from localStorage as a fallback
      if (typeof project === 'string' && !project.startsWith('local-')) {
        try {
          const backups = JSON.parse(localStorage.getItem('backupPlans') || '[]');
          // Try to find a backup with a similar name
          const matchingBackups = backups.filter(p => p.name.toLowerCase().includes(project.toLowerCase()));
          
          if (matchingBackups.length > 0) {
            handleLoadPlan(matchingBackups[0]);
          } else {
            onProjectAction('load', 'Error: Project not found in local backups');
          }
        } catch (e) {
          onProjectAction('load', 'Error: Could not load project');
        }
      } else {
        onProjectAction('load', `Error: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle deleting a project
  const handleDeletePlan = async (projectId) => {
    if (!user) {
      onProjectAction('delete', 'You must be logged in to delete plans');
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this plan?')) {
      return;
    }
    
    setDeleting(true);
    
    try {
      
      // Create a timeout promise
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Delete request timed out after 15 seconds')), 15000);
      });
      
      // Create the Supabase delete promise
      // eslint-disable-next-line no-unused-vars -- 3.4.2026
      const startTime = Date.now();
      const deletePromise = supabase
        .from('projects')
        .delete()
        .eq('id', projectId)
        .eq('user_id', user.id);
      
      // Race the delete operation against the timeout
      const result = await Promise.race([
        deletePromise,
        timeoutPromise.then(() => ({ data: null, error: new Error('Delete request timed out after 15 seconds') }))
      ]);
      
      const { error } = result;
      
      if (error) {
        throw error;
      }
      
      
      // Update local state
      setPlans(prevPlans => prevPlans.filter(p => p.id !== projectId));
      
      // Notify parent component that a project was deleted
      onProjectAction('delete', 'Plan deleted successfully');
    } catch (error) {
      onProjectAction('delete', `Error deleting project: ${error.message}`);
    } finally {
      setDeleting(false);
    }
  };

  // Handler for Create New Plan button
  const handleCreateNewPlan = () => {
    if (!user) {
      setShowLoginGate(true);
      return;
    }
    setShowCreateNewForm(true);
  };

  // Handler for Create New Plan form submission
  const handleCreateNewPlanSubmit = async (e) => {
    e.preventDefault();
    
    if (!planName.trim()) {
      showToast('Please enter a plan name', 'error');
      return;
    }

    if (!user) {
      showToast('You must be logged in to create a plan', 'error');
      return;
    }

    // Close the modal first
    setShowCreateNewForm(false);
    setSaving(true);

    try {
      // Clear all data first
      if (onClearAllData) {
        onClearAllData();
      }

      // Wait for state to update after clearing (React state updates are async)
      await new Promise(resolve => setTimeout(resolve, 300));

      // Create an empty/cleared state object to save
      const freshCookieId = `cookie_${Date.now()}`;
      const clearedState = {
        reference: { width: '', length: '', weightNoIcing: '', weight: '' },
        useDefaultReference: true,
        cookieRows: [{ id: freshCookieId, cookieName: '', type: 'rectangle', width: '', length: '', diameter: '', unit: 'in', notes: '', isMetric: false }],
        setRows: [{ id: `set_${Date.now()}`, setName: 'Set 1', cookieId: '', count: 1 }],
        dimensionsRows: [{ id: `dim_${Date.now()}`, cookieId: freshCookieId, shapeType: 'rectangle', width: '', length: '', diameter: '', area: 0, icingNeeded: 0 }],
        colorRows: [{ id: `col_${Date.now()}`, cookieId: freshCookieId, percentages: [100] }],
        colorNames: ['White'],
        orderRows: [{ id: `ord_${Date.now()}`, cookieId: freshCookieId, count: 0 }],
        detailAmounts: { detail: [0], floral: [0], other: [0] },
        useSets: false,
        isMetric: false,
        isMetricWeight: true
      };

      // Prepare the plan data with cleared state
      const planData = {
        user_id: user.id,
        name: planName.trim(),
        description: planDescription.trim(),
        data: JSON.stringify(clearedState),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      // Save to Supabase
      const { data, error } = await supabase
        .from('projects')
        .insert([planData])
        .select();

      if (error) {
        // Try to save to localStorage as backup
        saveToLocalStorage(planData);
        onProjectAction('save', 'Plan created locally (offline mode)');
      } else if (data && data.length > 0) {
        // Successfully saved to database
        setPlans(prevPlans => [data[0], ...prevPlans]);
        onProjectAction('save', `Plan "${planName.trim()}" created successfully`);
        
        // Refresh the plans list
        await fetchPlans();
      }

      // Reset form fields
      setPlanName('');
      setPlanDescription('');

      // Navigate to Sample Cookie section by triggering onLoadProject
      // Pass planId and planName so IcingPlansSection sets loadedPlanId/loadedPlanName in context
      if (onLoadProject) {
        const savedPlanId = data && data.length > 0 ? data[0].id : null;
        const savedPlanName = planName.trim();
        onLoadProject(clearedState, savedPlanId, savedPlanName, 'reference');
      }

    } catch (error) {
      showToast('Error creating plan: ' + error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <div className="project-manager">
      <div className="project-header">
        <button 
          className="create-new-plan-button" 
          onClick={handleCreateNewPlan}
          disabled={saving}
          style={{
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '4px',
            cursor: !saving ? 'pointer' : 'not-allowed',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          Create New Plan
        </button>
      </div>

      <div className="project-content">
          {showDuplicateForm && (
            <div className="modal-overlay" onClick={() => setShowDuplicateForm(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{
                backgroundColor: 'var(--background-color, white)',
                padding: '30px',
                borderRadius: '8px',
                maxWidth: '500px',
                width: '90%',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
              }}>
                <h2 style={{ marginTop: 0, marginBottom: '20px' }}>Duplicate Plan</h2>
                <p style={{ marginBottom: '20px', color: 'var(--text-secondary, #666)' }}>
                  Duplicating: <strong>{duplicatingPlan?.name}</strong>
                </p>
                <form onSubmit={handleDuplicateSubmit}>
                  <div className="form-group" style={{ marginBottom: '20px' }}>
                    <label htmlFor="duplicatePlanName" style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>New Plan Name</label>
                    <input
                      id="duplicatePlanName"
                      type="text"
                      value={duplicateName}
                      onChange={(e) => setDuplicateName(e.target.value)}
                      required
                      autoFocus
                      style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '14px', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: '20px' }}>
                    <label htmlFor="duplicatePlanDescription" style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Description (optional)</label>
                    <textarea
                      id="duplicatePlanDescription"
                      value={duplicateDescription}
                      onChange={(e) => setDuplicateDescription(e.target.value)}
                      rows="3"
                      style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '14px', resize: 'vertical', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      onClick={() => { setShowDuplicateForm(false); setDuplicatingPlan(null); }}
                      className="btn-secondary"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={duplicating}
                      className="btn-primary"
                    >
                      {duplicating ? 'Duplicating...' : 'Duplicate Plan'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {showCreateNewForm && (
            <div className="modal-overlay" style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}>
              <div className="modal-content" style={{
                backgroundColor: 'var(--background-color, white)',
                padding: '30px',
                borderRadius: '8px',
                maxWidth: '500px',
                width: '90%',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
              }}>
                <h2 style={{ marginTop: 0, marginBottom: '20px' }}>Create New Plan</h2>
                <form onSubmit={handleCreateNewPlanSubmit}>
                  <div className="form-group" style={{ marginBottom: '20px' }}>
                    <label htmlFor="newPlanName" style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Plan Name</label>
                    <input
                      id="newPlanName"
                      type="text"
                      value={planName}
                      onChange={(e) => setPlanName(e.target.value)}
                      autoFocus
                      required
                      style={{
                        width: '100%',
                        padding: '10px',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: '20px' }}>
                    <label htmlFor="newPlanDescription" style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Description (optional)</label>
                    <textarea
                      id="newPlanDescription"
                      value={planDescription}
                      onChange={(e) => setPlanDescription(e.target.value)}
                      rows="3"
                      style={{
                        width: '100%',
                        padding: '10px',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        fontSize: '14px',
                        resize: 'vertical'
                      }}
                    />
                  </div>
                  <div className="form-actions" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button 
                      type="button" 
                      onClick={() => {
                        setShowCreateNewForm(false);
                        setPlanName('');
                        setPlanDescription('');
                      }}
                      style={{
                        padding: '10px 20px',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        backgroundColor: 'transparent',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      style={{
                        padding: '10px 20px',
                        borderRadius: '4px',
                        border: 'none',
                        backgroundColor: '#28a745',
                        color: 'white',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                    >
                      Clear & Create Plan
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {showSaveForm && (
            <div className="modal-overlay" onClick={() => setShowSaveForm(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <h2>{currentPlanId ? 'Rename Plan' : 'Save Plan'}</h2>
                <form onSubmit={handleSavePlan}>
                  <div className="form-group" style={{ marginBottom: '20px' }}>
                    <label htmlFor="planName" style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Plan Name</label>
                    <input
                      id="planName"
                      type="text"
                      value={planName}
                      onChange={(e) => setPlanName(e.target.value)}
                      required
                      autoFocus
                      style={{
                        width: '100%',
                        padding: '10px',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: '20px' }}>
                    <label htmlFor="planDescription" style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Description (optional)</label>
                    <textarea
                      id="planDescription"
                      value={planDescription}
                      onChange={(e) => setPlanDescription(e.target.value)}
                      rows="3"
                      style={{
                        width: '100%',
                        padding: '10px',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        fontSize: '14px',
                        resize: 'vertical'
                      }}
                    />
                  </div>
                  <div className="form-actions" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button 
                      type="button" 
                      onClick={() => {
                        setShowSaveForm(false);
                        setPlanName('');
                        setPlanDescription('');
                        setCurrentPlanId(null);
                      }}
                      style={{
                        padding: '10px 20px',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        backgroundColor: 'transparent',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      disabled={saving}
                      style={{
                        padding: '10px 20px',
                        borderRadius: '4px',
                        border: 'none',
                        backgroundColor: '#3FA8AB',
                        color: 'white',
                        cursor: saving ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        opacity: saving ? 0.6 : 1
                      }}
                    >
                      {saving ? 'Saving...' : (currentPlanId ? 'Update Plan' : 'Save Plan')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {loading ? (
            <div className="loading">
              <div className="loading-spinner"></div>
              <div className="loading-text">Loading your plans...</div>
            </div>
          ) : (
            <>
              {/* Project List */}
              {plans && plans.length > 0 && (
                <>
                  <div className="plans-header">
                    <h3 className="projects-section-title">Your Plans</h3>
                    {!isMobileView && (
                    <div className="view-toggle-buttons">
                      <button
                        className={`view-toggle-btn ${viewMode === 'card' ? 'active' : ''}`}
                        onClick={() => { presMobileViewMode.current = 'card'; setViewMode('card'); }}
                        title="Card View"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="3" width="7" height="7"></rect>
                          <rect x="14" y="3" width="7" height="7"></rect>
                          <rect x="3" y="14" width="7" height="7"></rect>
                          <rect x="14" y="14" width="7" height="7"></rect>
                        </svg>
                      </button>
                      <button
                        className={`view-toggle-btn ${viewMode === 'row' ? 'active' : ''}`}
                        onClick={() => { presMobileViewMode.current = 'row'; setViewMode('row'); }}
                        title="Row View"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="3" y1="6" x2="21" y2="6"></line>
                          <line x1="3" y1="12" x2="21" y2="12"></line>
                          <line x1="3" y1="18" x2="21" y2="18"></line>
                        </svg>
                      </button>
                    </div>
                    )}
                  </div>
                  <div className={`project-list ${viewMode === 'row' ? 'row-view' : ''}`}>
                    {plans.map(project => (
                    <div 
                      key={project.id} 
                      className="project-item"
                      onClick={() => handleLoadPlan(project)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="project-info">
                        <h3>{project.name}</h3>
                        {project.description && <p>{project.description}</p>}
                        <div className="project-meta">
                          <span>Created: {new Date(project.created_at).toLocaleDateString()}</span>
                          {project.updated_at && (
                            <span>Updated: {new Date(project.updated_at).toLocaleDateString()}</span>
                          )}
                        </div>
                      </div>
                      <div className="project-actions">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLoadPlan(project, 'results');
                          }}
                          disabled={loading}
                          className="plan-btn-primary"
                          title="Load plan and go to results"
                        >
                          View
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLoadPlan(project, 'reference');
                          }}
                          disabled={loading}
                          className="plan-btn-secondary"
                          title="Load plan and go to page 1"
                        >
                          Edit
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDuplicatePlanClick(project);
                          }}
                          disabled={duplicating}
                          className="plan-btn-secondary"
                          title="Duplicate this plan"
                        >
                          Duplicate
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditPlanClick(project);
                          }}
                          disabled={saving}
                          className="plan-btn-secondary"
                          title="Rename this plan"
                        >
                          Rename
                        </button>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeletePlan(project.id);
                          }}
                          disabled={deleting}
                          className="btn-delete"
                          title="Remove this plan"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                  </div>
                </>
              )}

              {/* Local Backups */}
              {localBackups.length > 0 && (
                <>
                  <h3 className="projects-section-title">Local Backups</h3>
                  <div className="backup-info">
                    These are projects saved locally when the database connection was unavailable.
                    Loading them will attempt to sync them to your cloud storage.
                  </div>
                  {localBackups.map(backup => (
                    <div 
                      key={backup.id} 
                      className="project-item backup"
                      onClick={() => handleLoadPlan({...backup, isLocalBackup: true})}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="project-info">
                        <h3>{backup.name}</h3>
                        {backup.description && <p>{backup.description}</p>}
                        <div className="project-meta">
                          <span>Created: {new Date(backup.created_at).toLocaleDateString()}</span>
                          <span className="backup-badge">Local Backup</span>
                        </div>
                      </div>
                      <div className="project-actions">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLoadPlan({...backup, isLocalBackup: true});
                          }}
                          disabled={loading}
                          className="plan-btn-primary"
                        >
                          View
                        </button>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            // Remove from localStorage
                            const updatedBackups = localBackups.filter(p => p.id !== backup.id);
                            localStorage.setItem('backupPlans', JSON.stringify(updatedBackups));
                            setLocalBackups(updatedBackups);
                          }}
                          disabled={deleting}
                          className="btn-delete"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* No Plans */}
              {plans.length === 0 && localBackups.length === 0 && (
                <div className="no-projects">
                  {user ? (
                    'No saved plans. Create one by saving your current settings.'
                  ) : (
                    <>
                      You're not logged in, so plans can't be saved yet.{' '}
                      <a href="/login?tab=signup&redirect=/pro">Create a free account</a> to start saving your icing plans.
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
    </div>

      {/* Toast notification */}
      {toast && (
        <div className={`footer-toast footer-toast-${toast.type}`}>
          {toast.message}
        </div>
      )}

      <LoginGateModal
        open={showLoginGate}
        onClose={() => setShowLoginGate(false)}
        action="create your icing plan"
        subtitle="Create a free account to keep your icing plan saved and ready when you return. It only takes a few seconds, everything you've entered will stay right here, and you'll get the AI tools for free too."
      />
    </>
  );
};

export default ProjectManager;
